import * as jose from 'jose';
import { getApiConfig } from '../config.js';

const ACCESS_TOKEN_KEY = '_xma';
const REFRESH_TOKEN_KEY = '_xmr';
const EXPIRES_AT_KEY = '_xmet';

interface ExpiresAt {
  refreshTokenExpiresAt?: string | null;
}

interface PendingResolver {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

let isRefreshing = false;
let pendingQueue: PendingResolver[] = [];

const REFRESH_TIMEOUT = 10_000;

export type RefreshTokenErrorCode =
  | 'transient_error'
  | 'fatal_error'
  | 'no_marketplace_license_available'
  | 'no_named_license_available'
  | 'timeout'
  | 'invalid_response';

export class RefreshTokenError extends Error {
  readonly code: RefreshTokenErrorCode;
  constructor(code: RefreshTokenErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'RefreshTokenError';
    this.code = code;
  }
}

/**
 * Map a raw host error envelope `{status, body}` to a semantic RefreshTokenErrorCode.
 * The host forwards upstream auth-server errors verbatim; classification lives here.
 */
function classifyHostError(
  status: unknown,
  body: unknown,
): RefreshTokenErrorCode {
  const bodyStr = typeof body === 'string' ? body : '';
  if (bodyStr.includes('idp_integration_exchange_external_license_missing')) {
    return 'no_marketplace_license_available';
  }
  if (bodyStr.includes('idp_integration_exchange_named_license_limit_reached')) {
    return 'no_named_license_available';
  }
  const code = typeof status === 'number' ? status : NaN;
  if (code === 0 || code >= 500) return 'transient_error';
  return 'fatal_error';
}

function getStored(key: string): string | null {
  return localStorage.getItem(key);
}

function setStored(key: string, value: string): void {
  localStorage.setItem(key, value);
}

function removeStored(key: string): void {
  localStorage.removeItem(key);
}

function getJwtExpiration(token: string): string | null {
  try {
    const jwt = jose.decodeJwt(token);
    return jwt.exp ? new Date(jwt.exp * 1000).toISOString() : null;
  } catch {
    return null;
  }
}

export interface AccessTokenClaims {
  role?: string;
  xm_domain_id?: number;
  xm_domain_name?: string;
  [key: string]: unknown;
}

export function getAccessToken(): string | null {
  return getStored(ACCESS_TOKEN_KEY);
}

export function getAccessTokenClaims(): AccessTokenClaims | null {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jose.decodeJwt(token) as AccessTokenClaims;
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  return getStored(REFRESH_TOKEN_KEY);
}

export function setTokens(
  accessToken?: string,
  refreshToken?: string,
): void {
  const expiresAt: ExpiresAt = JSON.parse(getStored(EXPIRES_AT_KEY) ?? '{}');

  if (accessToken) {
    setStored(ACCESS_TOKEN_KEY, accessToken);
  }
  if (refreshToken) {
    setStored(REFRESH_TOKEN_KEY, refreshToken);
    expiresAt.refreshTokenExpiresAt = new Date(
      Date.now() + 90 * 24 * 60 * 60 * 1000,
    ).toISOString();
  }
  setStored(EXPIRES_AT_KEY, JSON.stringify(expiresAt));
}

export function clearTokens(): void {
  removeStored(ACCESS_TOKEN_KEY);
  removeStored(REFRESH_TOKEN_KEY);
  removeStored(EXPIRES_AT_KEY);
}

export function clearAccessToken(): void {
  removeStored(ACCESS_TOKEN_KEY);
}

export function isAccessTokenExpired(): boolean {
  const token = getAccessToken();
  if (!token) return true;
  const exp = getJwtExpiration(token);
  return exp ? Date.now() >= new Date(exp).getTime() : true;
}

export function getValidAccessToken(): string | null {
  const token = getAccessToken();
  return token && !isAccessTokenExpired() ? token : null;
}

/**
 * Request tokens from the host (parent window) via postMessage. The host
 * performs the upstream auth call and forwards the raw {status, body}
 * envelope verbatim; this function parses the body and classifies errors.
 */
function requestTokenFromHost(): Promise<string> {
  return new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID();
    const hostOrigin = getApiConfig().hostOrigin;

    const handler = (event: MessageEvent) => {
      if (hostOrigin && event.origin !== hostOrigin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.requestId !== requestId) return;
      if (data.type !== 'xmr:request-tokens-response') return;

      cleanup();

      const payload = data.payload;
      if (!payload || typeof payload !== 'object') {
        reject(new RefreshTokenError('invalid_response', 'Missing payload'));
        return;
      }

      const status = (payload as { status?: unknown }).status;
      const body = (payload as { body?: unknown }).body;
      if (typeof status !== 'number' || typeof body !== 'string') {
        reject(new RefreshTokenError('invalid_response', 'Missing status/body'));
        return;
      }

      if (status < 200 || status >= 300) {
        reject(new RefreshTokenError(classifyHostError(status, body), body));
        return;
      }

      let tokens: { access_token?: unknown; refresh_token?: unknown };
      try {
        tokens = JSON.parse(body);
      } catch {
        reject(new RefreshTokenError('invalid_response', 'Body is not JSON'));
        return;
      }
      const accessToken = tokens.access_token;
      const refreshToken = tokens.refresh_token;
      if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
        reject(new RefreshTokenError('invalid_response', 'Missing tokens'));
        return;
      }
      setTokens(accessToken, refreshToken);
      resolve(accessToken);
    };

    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('message', handler);
    };

    window.addEventListener('message', handler);
    window.parent.postMessage(
      {
        type: 'xmr:request-tokens',
        requestId,
        payload: { refreshToken: getRefreshToken() ?? undefined },
      },
      hostOrigin ?? '*',
    );

    const timer = setTimeout(() => {
      cleanup();
      reject(new RefreshTokenError('timeout', 'Token refresh timeout'));
    }, REFRESH_TIMEOUT);
  });
}

/**
 * Returns a valid access token. If the current token is expired, requests
 * new tokens from the host via postMessage. Concurrent callers are queued
 * so only one refresh request is made at a time.
 */
export async function ensureValidToken(): Promise<string> {
  const valid = getValidAccessToken();
  if (valid) return valid;

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      pendingQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await requestTokenFromHost();

    pendingQueue.forEach(p => p.resolve(newToken));
    pendingQueue = [];

    return newToken;
  } catch (error) {
    if (error instanceof RefreshTokenError && error.code === 'fatal_error') {
      clearTokens();
    }
    pendingQueue.forEach(p => p.reject(error));
    pendingQueue = [];
    throw error;
  } finally {
    isRefreshing = false;
  }
}
